import React from "react";
import { Switch, Route, Redirect } from "react-router-dom";
// creates a beautiful scrollar
import PerfectScrollbar from "perfect-scrollbar";
import "perfect-scrollbar/css/perfect-scrollbar.css";
// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import {
  DateRange,
  CalendarToday,
  Commute,
  Settings,
  Assignment,
  People,
  Category,
  FormatListBulleted
} from "@material-ui/icons/";
// core components
import Navbar from "components/Navbars/Navbar.js";
import Footer from "components/Footer/Footer.js";
import Sidebar from "components/Sidebar/Sidebar.js";
import styles from "assets/jss/material-dashboard-react/layouts/adminStyle.js";

import bgImage from "assets/img/sidebar-2.jpg";
import logo from "assets/img/logo2.png";

import Request from "views/Vehicle/Request.js";
import Home from "views/Vehicle/Home";
import MyVehicle from "views/Vehicle/MyVehicle";
import Accept from "views/Vehicle/Accept";
import VehicleTypeList from "../views/Vehicle/VehicleTypeList";
import VehicleList from "../views/Vehicle/VehicleList";
import VehicleOptionList from "../views/Vehicle/VehicleOptionList";
import VehicleQueueManage from "../views/Vehicle/VehicleQueueManage";
import VehicleDriverManager from "../views/Vehicle/VehicleDriverManager";
import Goto from "views/Vehicle/Goto";
import { getCurrentUser, isVehicleAdmin } from "views/Vehicle/vehicleAuth";

let ps;
const useStyles = makeStyles(styles);

export default function Vehicle({ ...rest }) {
  // styles
  const classes = useStyles();
  // ref to help us initialize PerfectScrollbar on windows devices
  const mainPanel = React.createRef();
  // states and functions
  const [image, setImage] = React.useState(bgImage);
  const [color, setColor] = React.useState("blue");
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const currentUser = getCurrentUser();
  const isAdmin = isVehicleAdmin(currentUser);

  // เมนูจัดเรียงใหม่ตามความต้องการ:
  // 1. ส่วนของผู้ใช้ทั่วไป: แบบจองรถ, ตารางการใช้รถของฉัน
  // 2. ส่วนของ Admin: ปฏิทินรถส่วนกลาง, จัดรถ, ตั้งค่า (เมนูย่อย)
  const routes = [
    // --- ส่วนของผู้ใช้ทั่วไป ---
    {
      name: "แบบจองรถ",
      icon: Commute,
      component: Request,
      layout: "/vehicle",
      path: "/request"
    },
    {
      name: "ตารางการใช้รถของฉัน",
      icon: CalendarToday,
      component: MyVehicle,
      layout: "/vehicle",
      path: "/me"
    },

    // --- ส่วนของ Admin (เฉพาะ DepartmentKey 140, 108 หรืองาน IT/Admin) ---
    ...(isAdmin
      ? [
          {
            name: "ปฏิทินรถส่วนกลาง",
            icon: DateRange,
            component: Home,
            layout: "/vehicle",
            path: "/index"
          },
          {
            name: "จัดรถ",
            icon: Commute,
            component: VehicleQueueManage,
            layout: "/vehicle",
            path: "/queuemanage"
          },
          {
            collapse: true,
            name: "ตั้งค่า",
            icon: Settings,
            state: "vehicleMasterDataCollapse",
            layout: "/vehicle",
            path: "/settings",
            views: [
              {
                name: "ข้อมูลรถ",
                icon: Assignment,
                component: VehicleList,
                layout: "/vehicle",
                path: "/vehiclelist"
              },
              {
                name: "จัดการคนขับรถ",
                icon: People,
                component: VehicleDriverManager,
                layout: "/vehicle",
                path: "/drivers"
              },
              {
                name: "ข้อมูลประเภทรถ",
                icon: Category,
                component: VehicleTypeList,
                layout: "/vehicle",
                path: "/vehicletypelist"
              },
              {
                name: "คำขอเพิ่มเติม",
                icon: FormatListBulleted,
                component: VehicleOptionList,
                layout: "/vehicle",
                path: "/vehicleoptionlist"
              }
            ]
          }
        ]
      : [])
  ];

  // Flat routes for Navbar breadcrumb/title
  const navbarRoutes = [];
  routes.forEach(r => {
    if (r.collapse && r.views) {
      r.views.forEach(v => navbarRoutes.push(v));
    } else {
      navbarRoutes.push(r);
    }
  });

  const switchRoutes = (
    <Switch>
      {/* 1. ส่วนของผู้ใช้ทั่วไป */}
      <Route path="/vehicle/request" component={Request} />
      <Route path="/vehicle/me" component={MyVehicle} />
      <Route path="/vehicle/myvehicle" component={MyVehicle} />

      {/* Internal / Link routes */}
      <Route path="/vehicle/accept/:PassengerID" component={Accept} />
      <Route path="/vehicle/accept/:AttendeeID" component={Accept} />
      <Route path="/vehicle/accept" component={Accept} />
      <Route path="/vehicle/id/:VBID" component={Goto} />

      {/* 2. ส่วนของ Admin (Protected Routes) */}
      <Route
        path="/vehicle/index"
        render={props =>
          isAdmin ? <Home {...props} /> : <Redirect to="/vehicle/request" />
        }
      />
      <Route
        path="/vehicle/queuemanage"
        render={props =>
          isAdmin ? <VehicleQueueManage {...props} /> : <Redirect to="/vehicle/request" />
        }
      />
      <Route
        path="/vehicle/vehiclelist"
        render={props =>
          isAdmin ? <VehicleList {...props} /> : <Redirect to="/vehicle/request" />
        }
      />
      <Route
        path="/vehicle/drivers"
        render={props =>
          isAdmin ? <VehicleDriverManager {...props} /> : <Redirect to="/vehicle/request" />
        }
      />
      <Route
        path="/vehicle/vehicletypelist"
        render={props =>
          isAdmin ? <VehicleTypeList {...props} /> : <Redirect to="/vehicle/request" />
        }
      />
      <Route
        path="/vehicle/vehicleoptionlist"
        render={props =>
          isAdmin ? <VehicleOptionList {...props} /> : <Redirect to="/vehicle/request" />
        }
      />

      {/* Default Landing: Admin ไปปฏิทินส่วนกลาง / ผู้ใช้ทั่วไป ไปแบบจองรถ */}
      <Redirect from="/vehicle" to={isAdmin ? "/vehicle/index" : "/vehicle/request"} />
    </Switch>
  );

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  const getRoute = () => {
    return window.location.pathname !== "/admin/maps";
  };
  const resizeFunction = () => {
    if (window.innerWidth >= 960) {
      setMobileOpen(false);
    }
  };

  // initialize and destroy the PerfectScrollbar plugin
  React.useEffect(() => {
    if (navigator.platform.indexOf("Win") > -1) {
      ps = new PerfectScrollbar(mainPanel.current, {
        suppressScrollX: true,
        suppressScrollY: false
      });
      document.body.style.overflow = "hidden";
    }
    window.addEventListener("resize", resizeFunction);
    return function cleanup() {
      if (navigator.platform.indexOf("Win") > -1) {
        ps.destroy();
      }
      window.removeEventListener("resize", resizeFunction);
    };
  }, [mainPanel]);

  return (
    <div className={classes.wrapper}>
      <Sidebar
        routes={routes}
        logoText={"ระบบจองรถ"}
        logo={logo}
        image={image}
        handleDrawerToggle={handleDrawerToggle}
        open={mobileOpen}
        color={color}
        {...rest}
      />
      <div className={classes.mainPanel} ref={mainPanel}>
        <Navbar
          routes={navbarRoutes}
          handleDrawerToggle={handleDrawerToggle}
          {...rest}
        />
        {getRoute() ? (
          <div className={classes.content}>
            <div className={classes.container}>{switchRoutes}</div>
          </div>
        ) : (
          <div className={classes.map}>{switchRoutes}</div>
        )}
        {getRoute() ? <Footer /> : null}
      </div>
    </div>
  );
}
